import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import type { PageResult, ProspectSummary } from "@/lib/types";

/**
 * Two prospects in one strategy may share an address on purpose: two source
 * rows that differ everywhere else are two leads, and each is contacted on
 * its own. That is easy to do by accident from the form, and invisible
 * afterwards. This says so, where the operator is about to do it and where
 * they would otherwise wonder why one person is getting two conversations.
 */
export function useSharedAddress(strategyId: string | null, email: string, excludeId?: string | null) {
  const normalised = email.trim().toLowerCase();
  const [settled, setSettled] = useState(normalised);
  useEffect(() => {
    const t = setTimeout(() => setSettled(normalised), 400);
    return () => clearTimeout(t);
  }, [normalised]);

  const looksLikeAnAddress = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(settled);
  const query = useQuery({
    queryKey: ["prospects-sharing", strategyId, settled],
    enabled: !!strategyId && looksLikeAnAddress,
    queryFn: () =>
      api.get<PageResult<ProspectSummary>>(
        `/api/v1/prospects?q=${encodeURIComponent(settled)}&strategyId=${strategyId}&size=20`,
      ),
  });
  const items = Array.isArray(query.data?.items) ? query.data.items : [];
  return items.filter((p) => p.email.toLowerCase() === settled && p.id !== (excludeId ?? null));
}

export default function SharedAddressNotice({
  strategyId,
  email,
  excludeId,
  variant,
}: {
  strategyId: string | null;
  email: string;
  excludeId?: string | null;
  /** `form`: about to create another; `detail`: looking at one of several. */
  variant: "form" | "detail";
}) {
  const others = useSharedAddress(strategyId, email, excludeId);
  if (others.length === 0) return null;
  const names = others.map((p) => p.fullName ?? p.email).join(", ");

  return (
    <div
      role="status"
      className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-900"
    >
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      {variant === "form" ? (
        <span>
          <span className="font-medium">This address already belongs to a prospect in this strategy</span>
          {": "}
          {others.map((p) => `${p.fullName ?? p.email} (${p.status}${p.currentPhaseSlug ? `, ${p.currentPhaseSlug}` : ""})`).join("; ")}
          . Creating another makes a separate lead that is contacted independently.
        </span>
      ) : (
        <span>
          <span className="font-medium">
            {others.length === 1 ? "One other prospect shares" : `${others.length} other prospects share`} this address
          </span>
          {" in this strategy: "}
          {names}. Each is contacted independently.
        </span>
      )}
    </div>
  );
}
